import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge.js';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { formatLabel } from '@/lib/utils.js';
import {
  type DefensibilityCriterion,
  type DefensibilityScore,
  ORDERED_CRITERIA,
  CRITERION_LABELS,
  SCORE_META,
  defensibilityKeys,
} from '@/lib/defensibility.js';

// ── Endpoint contracts (verified against artifacts/api-server/src/routes/defensibility.ts
//    and lib/db/src/schema/defensibility.ts) ──────────────────────────────────────────────
//   GET /api/projects/:id/defensibility
//     → { data: { checks: DefensibilityCheck[] } }
//   GET /api/projects/:id/defensibility/:criterion/evidence
//     → { data: { check: DefensibilityCheck | null, artefacts: EvidenceArtefact[] } }
//
//   Artefact rows carry `sourceDate` (nullable — the authoritative date the evidence CONTENT
//   pertains to, e.g. the git commit date) distinct from `createdAt` (when Edison ingested the
//   row). Consumers must display COALESCE(sourceDate, createdAt) — this is the contemporaneity
//   the evidence log exists to demonstrate.

interface DefensibilityCheck {
  id: string;
  projectId: string;
  criterion: DefensibilityCriterion;
  score: DefensibilityScore;
  rationale: string;
  gapsSummary?: string | null;
  suggestedActions?: string[];
  supportingArtefactIds: string[];
  isStale: boolean;
  refreshedAt: string;
}

interface EvidenceArtefact {
  id: string;
  title: string;
  type: string;
  /** @nullable */
  sourceDate?: string | null;
  createdAt: string;
}

function formatSourceDate(artefact: EvidenceArtefact): string {
  const date = artefact.sourceDate ?? artefact.createdAt;
  return new Date(date).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function CriterionEvidence({ projectId, criterion }: { projectId: string; criterion: DefensibilityCriterion }) {
  const evidenceQuery = useQuery({
    queryKey: defensibilityKeys.evidence(projectId, criterion),
    queryFn: async () => {
      const res = await customInstance<{ data: { check: DefensibilityCheck | null; artefacts: EvidenceArtefact[] } }>({
        url: `/api/projects/${projectId}/defensibility/${criterion}/evidence`,
        method: 'GET',
      });
      return res.data;
    },
  });

  if (evidenceQuery.isLoading) {
    return (
      <div className="space-y-2 pt-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (evidenceQuery.error) {
    return (
      <div className="pt-3">
        <InlineError
          message="Failed to load evidence for this criterion."
          onRetry={() => void evidenceQuery.refetch()}
        />
      </div>
    );
  }

  const artefacts = evidenceQuery.data?.artefacts ?? [];

  if (artefacts.length === 0) {
    return (
      <p className="pt-3 text-sm text-muted-foreground">No supporting evidence captured yet.</p>
    );
  }

  return (
    <ul className="pt-3 space-y-1.5">
      {artefacts.map((artefact) => (
        <li
          key={artefact.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline px-3 py-2 text-sm"
        >
          <span className="min-w-0 flex-1 truncate text-ink">{artefact.title}</span>
          <div className="flex shrink-0 items-center gap-2">
            <Badge>{formatLabel(artefact.type)}</Badge>
            <span className="text-xs text-muted-foreground">{formatSourceDate(artefact)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CriterionRow({ projectId, check }: { projectId: string; check: DefensibilityCheck }) {
  const [expanded, setExpanded] = useState(false);
  const meta = SCORE_META[check.score];
  const ScoreIcon = meta.Icon;

  // The clickable header must stay accessible (button + aria-expanded) but its CONTENT — the
  // criterion label, score badge, and rationale — must remain VISIBLE when the summary is
  // printed. So `no-print` lives ONLY on the chevron affordance, never on the button or its
  // content. Do not wrap the label/badge/rationale in a `no-print` element (a print regression).
  return (
    <div className="py-3">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 text-left"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="flex min-w-0 flex-1 items-start gap-3">
          {expanded ? (
            <ChevronDown className="no-print mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="no-print mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-ink">{CRITERION_LABELS[check.criterion]}</span>
              <Badge variant={meta.badgeVariant}>
                <ScoreIcon className="h-4 w-4" aria-hidden="true" />
                {meta.label}
              </Badge>
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">{check.rationale}</span>
          </span>
        </span>
      </button>
      {expanded && <CriterionEvidence projectId={projectId} criterion={check.criterion} />}
    </div>
  );
}

export function EvidenceLog({ projectId }: { projectId: string }) {
  const checksQuery = useQuery({
    queryKey: defensibilityKeys.checks(projectId),
    queryFn: async () => {
      const res = await customInstance<{ data: { checks: DefensibilityCheck[] } }>({
        url: `/api/projects/${projectId}/defensibility`,
        method: 'GET',
      });
      return res.data;
    },
  });

  if (checksQuery.isLoading) {
    return (
      <div className="space-y-2" data-testid="evidence-log-loading">
        <SkeletonText lines={2} />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (checksQuery.error) {
    return (
      <InlineError
        message="Failed to load the evidence log for this project."
        onRetry={() => void checksQuery.refetch()}
      />
    );
  }

  const checks = checksQuery.data?.checks ?? [];
  const checkMap = new Map(checks.map((c) => [c.criterion, c]));
  const orderedChecks = ORDERED_CRITERIA.map((c) => checkMap.get(c)).filter(
    (c): c is DefensibilityCheck => Boolean(c),
  );

  if (orderedChecks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No defensibility checks have been run for this project yet.
      </p>
    );
  }

  return (
    <div className="divide-y divide-border/40">
      {orderedChecks.map((check) => (
        <CriterionRow key={check.criterion} projectId={projectId} check={check} />
      ))}
    </div>
  );
}
